/* eslint-disable @typescript-eslint/no-explicit-any */
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';

const mContext: any = createContext;

const TextRevealContext = mContext();

// Throttled scroll hook to reduce main thread work
function useThrottledScroll(delay = 16) {
  const [scrollY, setScrollY] = useState(0);
  const lastUpdate = useRef(0);
  const rafId = useRef<number | undefined>(undefined);

  useEffect(() => {
    const handleScroll = () => {
      const now = Date.now();
      if (now - lastUpdate.current >= delay) {
        lastUpdate.current = now;
        setScrollY(window.scrollY);
      } else if (!rafId.current) {
        rafId.current = requestAnimationFrame(() => {
          lastUpdate.current = Date.now();
          setScrollY(window.scrollY);
          rafId.current = undefined;
        });
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafId.current) cancelAnimationFrame(rafId.current);
    };
  }, [delay]);

  return scrollY;
}

function Root({ body = '', children, className, as: Component = 'div', ...props }: any) {
  const container = useRef(null);

  const windowScroll = useThrottledScroll(32); // Throttle to ~30fps

  const top = useMemo(() => {
    return (container.current as any)?.getBoundingClientRect().top;
  }, [windowScroll]);

  const height = useMemo(() => {
    return (container.current as any)?.getBoundingClientRect().height;
  }, [windowScroll]);

  const tokens = useMemo(() => body.match(/\S+|\s+/g) || [], [body]);

  const progress = useMemo(() => (-top / height) * 2, [top, height]);

  const context = {
    tokens,
    progress,
  };

  return (
    <TextRevealContext.Provider value={context}>
      <Component ref={container} className={className} {...props}>
        {children(tokens, progress)}
      </Component>
    </TextRevealContext.Provider>
  );
}

function Token({ index, children }: any) {
  const context = useContext(TextRevealContext) as any;

  const isActive = useMemo(() => index / context.tokens.length < context.progress, [index, context.progress]);

  return <>{children(isActive)}</>;
}

export const TextReveal = Object.assign(Root, { Token });
